<script>
import store from '@state/store'

export default {
  name: 'CinematicLoader',
  props: {
    loading: Boolean,
    size: {
      type: [Number, String],
      default: 240,
    },
  },
  data() {
    return {
      imageFailed: false,
    }
  },
  computed: {
    // Fundo = cor do cabeçalho da navbar (bg-navbar-header)
    background() {
      return this.$Color()['bg-navbar-header'] || '#FFFFFF'
    },
    logoSource() {
      return store.getters['parameters/logo']
    },
    logoStyle() {
      const s = typeof this.size === 'number' ? this.size + 'px' : this.size
      return { width: s }
    },
    showLogo() {
      return !!this.logoSource && !this.imageFailed
    },
  },
  watch: {
    logoSource() {
      this.imageFailed = false
    },
  },
  methods: {
    onImageError() {
      this.imageFailed = true
    },
  },
}
</script>

<template>
  <v-fade-transition>
    <div v-if="loading" class="cinematic-loader" :style="{ background }">
      <div class="cinematic-loader__aurora cinematic-loader__aurora--a" />
      <div class="cinematic-loader__aurora cinematic-loader__aurora--b" />

      <div class="cinematic-loader__content">
        <div
          v-if="showLogo"
          class="cinematic-loader__logo-wrap"
          :style="logoStyle"
        >
          <img
            class="cinematic-loader__logo cinematic-loader__logo--ghost"
            :src="logoSource"
            alt=""
            @error="onImageError"
          />
          <img
            class="cinematic-loader__logo cinematic-loader__logo--fill"
            :src="logoSource"
            alt=""
            aria-hidden="true"
            @error="onImageError"
          />
        </div>

        <v-progress-circular
          v-else
          class="cinematic-loader__spinner"
          indeterminate
          color="primary"
          :size="56"
          :width="4"
        />
      </div>
    </div>
  </v-fade-transition>
</template>

<style lang="sass" scoped>
$accent: var(--v-primary-base, #56AF79)

// left-to-right opacity reveal that loops without a hard reset
=fill-sweep
  animation: cinematicFill 2.2s cubic-bezier(0.4, 0, 0.2, 1) infinite

.cinematic-loader
  position: fixed
  top: 0
  left: 0
  width: 100%
  height: 100%
  z-index: 9999
  overflow: hidden
  display: flex
  align-items: center
  justify-content: center
  color: $accent
  background: #FFFFFF

  // soft brand-colored glows drifting behind the logo
  &__aurora
    position: absolute
    border-radius: 50%
    filter: blur(110px)
    background: radial-gradient(circle at center, currentColor 0%, transparent 70%)
    will-change: transform, opacity

    &--a
      width: 42vw
      height: 42vw
      top: -10vw
      left: -8vw
      opacity: 0.32
      animation: cinematicDriftA 18s ease-in-out infinite

    &--b
      width: 38vw
      height: 38vw
      bottom: -12vw
      right: -10vw
      opacity: 0.24
      animation: cinematicDriftB 22s ease-in-out infinite

  &__content
    position: relative
    z-index: 1
    display: flex
    align-items: center
    justify-content: center

  &__logo-wrap
    position: relative
    display: inline-flex
    max-width: 62vw

  &__logo
    display: block
    width: 100%
    height: auto
    max-height: 30vh
    object-fit: contain

  // faint base copy, always visible
  &__logo--ghost
    opacity: 0.16

  // bright copy revealed left-to-right by the animated clip
  &__logo--fill
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    opacity: 1
    +fill-sweep

@keyframes cinematicFill
  0%
    clip-path: inset(0 100% 0 0)
    opacity: 1
  60%
    clip-path: inset(0 0 0 0)
    opacity: 1
  80%
    clip-path: inset(0 0 0 0)
    opacity: 1
  100%
    clip-path: inset(0 0 0 0)
    opacity: 0

@keyframes cinematicDriftA
  0%, 100%
    transform: translate(0, 0) scale(1)
  50%
    transform: translate(7vw, 5vw) scale(1.12)

@keyframes cinematicDriftB
  0%, 100%
    transform: translate(0, 0) scale(1)
  50%
    transform: translate(-6vw, -4vw) scale(1.15)

@media (prefers-reduced-motion: reduce)
  .cinematic-loader__aurora,
  .cinematic-loader__logo--fill
    animation: none
  .cinematic-loader__logo--fill
    clip-path: none
    opacity: 1
</style>
